import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    Wrapper,
    Input,
    DropdownContainer,
    DropdownItem,
    DropdownItemDescription,
    DropdownItemLabel,
    DropdownItemHeader,
    DropdownItemBadge,
} from "./Styled";

export interface TextAndDropdownOption {
    text: string;
    value: string;
    label?: string;
    description?: string;
    key?: string;
    badge?: string;
}

interface TextAndDropdownWithSearchProps {
    menuList: TextAndDropdownOption[];
    menuText: string;
    handleOnclickItem: (item: any) => void;
    onInputChange: (value: string) => void;
    fetchItems?: (search: string) => Promise<any[]>;
    value: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    // Add cache for UUID resolution
    uuidCache?: Map<string, { text: string; value: string }>;
    dropdownContainerStyle?: React.CSSProperties;
    ariaLabel?: string;
}

const matchesSearch = (item: TextAndDropdownOption, search: string) =>
    [item.text, item.label, item.description, item.badge]
        .filter(Boolean)
        .some((candidate) => candidate!.toLowerCase().includes(search.toLowerCase()));

const TextAndDropdownWithSearch: React.FC<TextAndDropdownWithSearchProps> = ({
    menuList,
    menuText,
    handleOnclickItem,
    onInputChange,
    fetchItems,
    value,
    disabled,
    style,
    uuidCache,
    dropdownContainerStyle,
    ariaLabel,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || "");
    const [filteredMenuList, setFilteredMenuList] = useState(menuList); // Dynamically updated menu list
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchTextRef = useRef<string | null>(null);

    useEffect(() => {
        if (!fetchItems) {
            const search = searchTextRef.current;
            setFilteredMenuList(search === null ? menuList : menuList.filter((item) => matchesSearch(item, search)));
        }
    }, [menuList, fetchItems]);


    useEffect(() => {
        // Only update inputValue if it's not a UUID (car plate case)
        // UUIDs are typically long strings with dashes, so we check if it looks like a UUID
        const isUUID = value && value.length > 20 && value.includes('-');
        if (!isUUID) {
            setInputValue(value || "");
        } else {
            // If it's a UUID, try to resolve it from cache first, then fallback to API
            if (uuidCache) {
                const cached = uuidCache.get(value);
                if (cached) {
                    setInputValue(cached.text);
                    return;
                }
            }
            
            // Fallback to API call if not in cache
            if (fetchItems) {
                fetchItems("").then((items) => {
                    const foundItem = items.find((item: any) => item.value === value);
                    if (foundItem) {
                        setInputValue(foundItem.text);
                    } else {
                        setInputValue(value || ""); // Fallback to UUID if not found
                    }
                }).catch(() => {
                    setInputValue(value || ""); // Fallback to UUID if error
                });
            } else {
                setInputValue(value || ""); // Fallback to UUID if no fetchItems
            }
        }
    }, [value, fetchItems, uuidCache]);

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        searchTextRef.current = newValue;
        setInputValue(newValue);
        onInputChange(newValue); // Notify parent about the input change

        // Fetch items dynamically if `fetchItems` is provided
        if (fetchItems) {
            const fetchedItems = await fetchItems(newValue);
            setFilteredMenuList(fetchedItems);
        } else {
            // Filter the existing menu list if no `fetchItems` function is provided
            setFilteredMenuList(
                menuList.filter((item) => matchesSearch(item, newValue))
            );
        }
    };

    const updateDropdownPosition = () => {
        if (!inputRef.current) return;

        const rect = inputRef.current.getBoundingClientRect();
        setDropdownStyle({
            position: "fixed",
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            zIndex: 9999,
            ...dropdownContainerStyle,
        });
    };

    const handleSelectItem = (item: any) => {
        searchTextRef.current = null;
        setInputValue(item.text); // Update input value with selected item
        handleOnclickItem(item); // Notify parent about the selection
        setIsOpen(false); // Close the dropdown
    };

    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        // Check if click is outside both wrapper and dropdown
        const dropdownElement = document.querySelector('[data-dropdown="true"]');
        if (
            wrapperRef.current && 
            !wrapperRef.current.contains(target) &&
            (!dropdownElement || !dropdownElement.contains(target))
        ) {
            setIsOpen(false); // Close the dropdown if clicked outside
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            <Wrapper ref={wrapperRef} style={style}>
                <Input
                    ref={inputRef}
                    aria-label={ariaLabel}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={async () => {
                        searchTextRef.current = null;
                        updateDropdownPosition();
                        setIsOpen(true);
                        // Trigger initial fetch with empty search on first open
                        if (fetchItems && (filteredMenuList.length === 0 || inputValue.length === 0)) {
                            try {
                                const initialItems = await fetchItems("");
                                setFilteredMenuList(initialItems);
                            } catch {
                                setFilteredMenuList([]);
                            }
                        } else {
                            setFilteredMenuList(menuList || []);
                        }
                    }}
                    disabled={disabled}
                    placeholder={menuText}
                />
            </Wrapper>
            {isOpen && filteredMenuList.length > 0 &&
                createPortal(
                    <DropdownContainer style={dropdownStyle} data-dropdown="true">
                        {filteredMenuList.map((option) => (
                            <DropdownItem
                                key={option.key || option.value}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelectItem(option);
                                }}
                            >
                                {option.label ? (
                                    <>
                                        <DropdownItemHeader>
                                            <DropdownItemLabel>{option.label}</DropdownItemLabel>
                                            {option.badge && <DropdownItemBadge>{option.badge}</DropdownItemBadge>}
                                        </DropdownItemHeader>
                                        <DropdownItemDescription>
                                            {option.description || option.text}
                                        </DropdownItemDescription>
                                    </>
                                ) : (
                                    <DropdownItemHeader>
                                        <span>{option.text}</span>
                                        {option.badge && <DropdownItemBadge>{option.badge}</DropdownItemBadge>}
                                    </DropdownItemHeader>
                                )}
                            </DropdownItem>
                        ))}
                    </DropdownContainer>,
                    document.body
                )}
        </>
    );
};

export default TextAndDropdownWithSearch;
